import { css } from '@emotion/react';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Checkbox, Text } from '@frontend/design-system';

export interface OptionType {
  label: string;
  value: string;
  archived?: boolean;
}

interface DropdownOptionProps {
  option: OptionType;
  selected: boolean;
  onChange: (option: OptionType) => void;
}

export const DropdownOption = ({ option, selected, onChange }: DropdownOptionProps) => {
  const id = `checkbox-${option.value}`;
  return (
    <div
      tabIndex={0}
      role='option'
      aria-selected={selected}
      css={dropdownOptionStyle(option.archived && !selected)}
      onClick={() => {
        if (option.archived && !selected) {
          return;
        }
        onChange(option);
      }}
      onKeyDown={(e) => {
        e.preventDefault();
        if (e.key === 'Tab') {
          // focus on search input
          const searchInput = document.querySelector('input[name="search-field"]') as HTMLInputElement;
          searchInput.focus();
        }
        if (e.key === 'Enter' || e.key === ' ') {
          if (option.archived && !selected) {
            return;
          }
          onChange(option);
        } else if (e.key === 'ArrowDown') {
          const focusedElement = document.activeElement;
          const nextElement = focusedElement
            ?.closest('li')
            ?.nextElementSibling?.querySelector('[role="option"]') as HTMLElement;

          // Focus on the next sibling if available
          if (nextElement) {
            nextElement.focus();
          }
        } else if (e.key === 'ArrowUp') {
          const focusedElement = document.activeElement;
          const prevElement = focusedElement
            ?.closest('li')
            ?.previousElementSibling?.querySelector('[role="option"]') as HTMLElement;

          // Focus on the previous sibling if available
          if (prevElement) {
            prevElement.focus();
          }
        }
      }}
    >
      <Checkbox
        name={id}
        id={id}
        aria-invalid={false}
        value={selected}
        // passing empty function as a placeholder for the event handlers
        // as user interaction is handled by parent div
        onBlur={() => {}}
        onChange={() => {}}
        onFocus={() => {}}
        error={false}
        active={false}
        tabIndex={-1}
        disabled={option.archived && !selected}
      />
      <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(0.5) }}>
        {option.archived && <Icon size={18} name='archive-small' color='subdued' />}
        <Text color={option.archived ? 'subdued' : 'default'}>{option.label}</Text>
      </div>
    </div>
  );
};

const dropdownOptionStyle = (disabled?: boolean) =>
  css({
    display: 'flex',
    alignItems: 'center',
    margin: theme.spacing(1),
    padding: theme.spacing(1, 1),
    gap: theme.spacing(1),
    cursor: disabled ? 'not-allowed' : 'pointer',
    ...(!disabled && {
      ':hover': {
        background: theme.colors.neutral5,
      },
    }),
  });
